import { useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import * as THREE from "three";
import {
  PRESET_GRID_BOUNDARY_YAWS,
  PRESET_GRID_MARKER_SIZE_PX,
  presetGridBoundaryPitches,
  presetGridCenter360,
} from "../../../lib/presetGrid.ts";
import { patrolHotspotWorldPosition } from "./patrolHotspotProjection.js";
import { projectPatrolWorldToHostPx } from "./patrolPanoramaFocusRing.js";

const SPHERE_R = 100;
const OUTLINE_STEPS = 84;
const OUTLINE_PITCH_MIN = -80;
const OUTLINE_PITCH_MAX = 80;
const SEGMENT_BREAK_PX_FRAC = 0.35;

const _outlineWorld = new THREE.Vector3();
const _camForward = new THREE.Vector3();
const _sphereDir = new THREE.Vector3();

function applyPresetMarkerStyle(el, selected) {
  Object.assign(el.style, {
    position: "absolute",
    borderRadius: "50%",
    border: selected
      ? "2px solid #FACC15"
      : "2px solid rgba(255,255,255,0.9)",
    background: selected ? "rgba(37,99,235,0.96)" : "rgba(15,23,42,0.8)",
    color: "#fff",
    fontWeight: "700",
    fontSize: "13px",
    lineHeight: "1",
    padding: "0",
    boxShadow: "0 0 0 2px rgba(0,0,0,0.35)",
    pointerEvents: "auto",
    cursor: "pointer",
    zIndex: "9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transform: "translate(-50%, -50%)",
  });
}

/**
 * Immersive 360° preset grid lines + markers for DB-saved presets only (wankhede-360).
 */
export function PatrolPresetGridOverlay({
  enabled,
  canvasRef,
  cameraRef,
  hostRef,
  viewerRotation,
  mappedPresetGridIds = [],
  selectedPresetGridId = null,
  onPresetGridSelect,
}) {
  const markerLayerRef = useRef(null);
  const markerElsRef = useRef(new Map());
  const mappedSetRef = useRef(new Set());
  const onSelectRef = useRef(onPresetGridSelect);
  const selectedIdRef = useRef(selectedPresetGridId);

  onSelectRef.current = onPresetGridSelect;
  selectedIdRef.current = selectedPresetGridId;
  mappedSetRef.current = new Set(mappedPresetGridIds);

  useEffect(() => {
    if (!enabled) return undefined;

    const host = hostRef.current;
    if (!host) return undefined;

    const markerLayer = document.createElement("div");
    markerLayer.className = "patrol-preset-grid-layer";
    Object.assign(markerLayer.style, {
      position: "absolute",
      inset: "0",
      overflow: "hidden",
      pointerEvents: "none",
      zIndex: "7",
    });
    host.appendChild(markerLayer);
    markerLayerRef.current = markerLayer;

    const outlineSvg = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "svg",
    );
    Object.assign(outlineSvg.style, {
      position: "absolute",
      inset: "0",
      width: "100%",
      height: "100%",
      pointerEvents: "none",
      overflow: "visible",
    });
    markerLayer.appendChild(outlineSvg);

    const yawPaths = PRESET_GRID_BOUNDARY_YAWS.map(() => {
      const path = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "path",
      );
      path.setAttribute("fill", "none");
      path.setAttribute("stroke", "rgba(255,255,255,0.55)");
      path.setAttribute("stroke-width", "1");
      outlineSvg.appendChild(path);
      return path;
    });

    const pitchPaths = presetGridBoundaryPitches().map(() => {
      const path = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "path",
      );
      path.setAttribute("fill", "none");
      path.setAttribute("stroke", "rgba(255,255,255,0.55)");
      path.setAttribute("stroke-width", "1");
      outlineSvg.appendChild(path);
      return path;
    });

    const syncOutline = (camera, canvas) => {
      const hostW = Math.max(host.clientWidth, 1);
      const hostH = Math.max(host.clientHeight, 1);
      outlineSvg.setAttribute("width", String(hostW));
      outlineSvg.setAttribute("height", String(hostH));
      outlineSvg.style.display = "block";
      camera.updateMatrixWorld(true);
      camera.getWorldDirection(_camForward);
      const breakPx = hostW * SEGMENT_BREAK_PX_FRAC;

      const projectPct = (world) => {
        const proj = projectPatrolWorldToHostPx(canvas, camera, host, world);
        if (!proj.visible) return null;
        return { x: proj.left, y: proj.top };
      };

      PRESET_GRID_BOUNDARY_YAWS.forEach((yawDeg, idx) => {
        const path = yawPaths[idx];
        if (!path) return;
        const segments = [];
        let current = [];

        for (let i = 0; i <= OUTLINE_STEPS; i++) {
          const t = i / OUTLINE_STEPS;
          const pitchDeg =
            OUTLINE_PITCH_MIN + (OUTLINE_PITCH_MAX - OUTLINE_PITCH_MIN) * t;
          patrolHotspotWorldPosition(yawDeg, pitchDeg, SPHERE_R, _outlineWorld);
          _sphereDir.copy(_outlineWorld).normalize();
          if (_sphereDir.dot(_camForward) <= 0.04) {
            if (current.length) {
              segments.push(current);
              current = [];
            }
            continue;
          }
          const pt = projectPct(_outlineWorld);
          if (!pt) {
            if (current.length) {
              segments.push(current);
              current = [];
            }
            continue;
          }
          if (current.length) {
            const prev = current[current.length - 1];
            if (Math.hypot(pt.x - prev.x, pt.y - prev.y) > breakPx) {
              segments.push(current);
              current = [pt];
              continue;
            }
          }
          current.push(pt);
        }
        if (current.length) segments.push(current);

        const d = segments
          .map((pts) =>
            pts
              .map(
                (p, pointIdx) =>
                  `${pointIdx === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`,
              )
              .join(" "),
          )
          .join(" ");
        path.setAttribute("d", d);
        path.style.display = d ? "block" : "none";
      });

      presetGridBoundaryPitches().forEach((pitchDeg, idx) => {
        const path = pitchPaths[idx];
        if (!path) return;
        const segments = [];
        let current = [];

        for (let i = 0; i <= OUTLINE_STEPS; i++) {
          const t = i / OUTLINE_STEPS;
          const yawDeg = -180 + t * 360;
          patrolHotspotWorldPosition(yawDeg, pitchDeg, SPHERE_R, _outlineWorld);
          _sphereDir.copy(_outlineWorld).normalize();
          if (_sphereDir.dot(_camForward) <= 0.04) {
            if (current.length) {
              segments.push(current);
              current = [];
            }
            continue;
          }
          const pt = projectPct(_outlineWorld);
          if (!pt) {
            if (current.length) {
              segments.push(current);
              current = [];
            }
            continue;
          }
          if (current.length) {
            const prev = current[current.length - 1];
            if (Math.hypot(pt.x - prev.x, pt.y - prev.y) > breakPx) {
              segments.push(current);
              current = [pt];
              continue;
            }
          }
          current.push(pt);
        }
        if (current.length) segments.push(current);

        const d = segments
          .map((pts) =>
            pts
              .map(
                (p, pointIdx) =>
                  `${pointIdx === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`,
              )
              .join(" "),
          )
          .join(" ");
        path.setAttribute("d", d);
        path.style.display = d ? "block" : "none";
      });
    };

    const syncMarkers = (camera, canvas) => {
      const gridIds = [...mappedSetRef.current].sort((a, b) => a - b);
      const activeIds = new Set(
        gridIds.map((gridId) => `__preset_grid_${gridId}`),
      );

      for (const [id, el] of [...markerElsRef.current.entries()]) {
        if (!activeIds.has(id)) {
          el.remove();
          markerElsRef.current.delete(id);
        }
      }

      for (const gridId of gridIds) {
        const id = `__preset_grid_${gridId}`;
        const { yawDeg, pitchDeg } = presetGridCenter360(gridId);
        const world = new THREE.Vector3();
        patrolHotspotWorldPosition(yawDeg, pitchDeg, SPHERE_R, world);
        const selected = selectedIdRef.current === gridId;

        let el = markerElsRef.current.get(id);
        if (!el) {
          el = document.createElement("button");
          el.type = "button";
          el.addEventListener("click", (ev) => {
            ev.stopPropagation();
            onSelectRef.current?.(gridId);
          });
          el.addEventListener("pointerdown", (ev) => ev.stopPropagation());
          el.addEventListener("pointerup", (ev) => ev.stopPropagation());
          markerLayer.appendChild(el);
          markerElsRef.current.set(id, el);
        }

        applyPresetMarkerStyle(el, selected);
        el.textContent = String(gridId);
        el.title = `Preset grid ${gridId}`;
        el.setAttribute("aria-label", `Grid ${gridId}`);

        const proj = projectPatrolWorldToHostPx(canvas, camera, host, world);
        if (!proj.visible) {
          el.style.display = "none";
          continue;
        }
        const size = PRESET_GRID_MARKER_SIZE_PX;
        el.style.display = "flex";
        el.style.left = `${proj.left}px`;
        el.style.top = `${proj.top}px`;
        el.style.width = `${size}px`;
        el.style.height = `${size}px`;
      }
    };

    let raf = 0;
    const tick = () => {
      const canvas = canvasRef.current;
      const camera = cameraRef.current;
      if (canvas && camera) {
        camera.rotation.order = "YXZ";
        camera.rotation.y = THREE.MathUtils.degToRad(viewerRotation.yaw);
        camera.rotation.x = THREE.MathUtils.degToRad(viewerRotation.pitch);
        camera.fov = viewerRotation.fov;
        camera.updateProjectionMatrix();
        syncOutline(camera, canvas);
        syncMarkers(camera, canvas);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      for (const el of markerElsRef.current.values()) {
        el.remove();
      }
      markerElsRef.current.clear();
      outlineSvg.remove();
      markerLayer.remove();
      markerLayerRef.current = null;
    };
  }, [
    enabled,
    canvasRef,
    cameraRef,
    hostRef,
    viewerRotation.yaw,
    viewerRotation.pitch,
    viewerRotation.fov,
    mappedPresetGridIds,
  ]);

  if (!enabled) return null;

  return (
    <Box
      className="patrol-preset-grid-overlay"
      sx={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: 7,
      }}
      aria-hidden
    />
  );
}
